import React from "react";
import { Share2, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ShareReactBar — bouton « Partager » (Web Share API + fallback copie) +
 * bouton « Réagir par email » (mailto: vers l'adresse configurée dans
 * l'admin SiteConfig > FAB > contact_email).
 *
 * Le lien partagé est tracké via paramètres UTM
 * (utm_source=share, utm_medium={kind}, utm_campaign={slug}) pour mesurer
 * la traction de chaque publication dans Plausible / GA.
 *
 * Props :
 *  - title  : titre de la publication (utilisé dans le mailto et le partage)
 *  - kind   : libellé court du type ("Action", "Article", "Ressource", "Consultation")
 */
function slugify(s = "") {
  return s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function buildTrackedUrl(rawUrl, kind, title) {
  try {
    const u = new URL(rawUrl);
    u.searchParams.set("utm_source", "share");
    u.searchParams.set("utm_medium", (kind || "publication").toLowerCase());
    if (title) u.searchParams.set("utm_campaign", slugify(title));
    return u.toString();
  } catch {
    return rawUrl;
  }
}

export default function ShareReactBar({ title = "", kind = "publication" }) {
  const [email, setEmail] = React.useState("info@lecef.org");
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    api.get("/site-config")
      .then((r) => { if (r.data?.fab?.contact_email) setEmail(r.data.fab.contact_email); })
      .catch(() => {});
  }, []);

  const rawUrl = typeof window !== "undefined" ? window.location.href : "";
  const trackedUrl = buildTrackedUrl(rawUrl, kind, title);

  const onShare = async () => {
    const payload = { title: `Élève ta voix · ${title}`, text: title, url: trackedUrl };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch { /* user cancel: fall through */ }
    }
    try {
      await navigator.clipboard.writeText(trackedUrl);
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de partager. Copie le lien depuis la barre d'adresse.");
    }
  };

  const mailto = (() => {
    const subject = `[Élève ta voix] Réaction · ${kind} : ${title}`;
    const body = `Bonjour,\n\nJe réagis à cette publication : ${title}\n${trackedUrl}\n\nMon message :\n`;
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  })();

  return (
    <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="share-react-bar">
      <button
        type="button"
        onClick={onShare}
        className="neo-btn bg-cef-blue text-white"
        data-testid="share-btn"
        aria-label="Partager cette publication"
      >
        {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
        {copied ? "Lien copié" : "Partager"}
      </button>
      <a
        href={mailto}
        className="neo-btn bg-cef-pink text-white"
        data-testid="react-email-btn"
        aria-label={`Réagir par email à ${email}`}
      >
        <Mail className="w-4 h-4" /> Réagir par email
      </a>
      <span className="text-xs text-neutral-500" data-testid="contact-email-hint">→ {email}</span>
    </div>
  );
}
